/**
 * The embed's light/dark theme. Dark is the original look; light suits a white
 * page. The server stamps the choice on `<html data-embed-theme>` (so there is
 * no dark flash before the app mounts) and hands it to the app in
 * `window.embedConfig.theme`; the panels read their colours from the CSS
 * variables in embed_theme.css, and the elevation chart, which draws on a
 * canvas, reads `elevationProfilePalette`.
 */
import type { EmbedTheme } from './embedUrlParams';

export function normalizeEmbedTheme(value: unknown): EmbedTheme {
  return value === 'light' ? 'light' : 'dark';
}

/** The theme this embed was asked for; dark for pages rendered before themes existed. */
export function currentEmbedTheme(win: { embedConfig?: { theme?: unknown } } = window as any): EmbedTheme {
  return normalizeEmbedTheme(win.embedConfig?.theme);
}

/** The colours the elevation chart paints with, which CSS variables can't reach. */
export interface ElevationProfilePalette {
  tooltipBackground: string;
  tooltipText: string;
  grid: string;
  tick: string;
  /** "High point · 412 m" above the plot. */
  highPointLabel: string;
  /** The place names beside the A and B badges. */
  endpointName: string;
}

const PALETTES: Record<EmbedTheme, ElevationProfilePalette> = {
  dark: {
    tooltipBackground: 'rgba(40, 42, 50, 0.95)',
    tooltipText: '#fff',
    grid: 'rgba(255, 255, 255, 0.1)',
    tick: 'rgba(255, 255, 255, 0.7)',
    highPointLabel: 'rgba(255, 255, 255, 0.65)',
    endpointName: 'rgba(255, 255, 255, 0.85)',
  },
  light: {
    tooltipBackground: 'rgba(255, 255, 255, 0.97)',
    tooltipText: '#1a1b21',
    grid: 'rgba(0, 0, 0, 0.1)',
    tick: 'rgba(26, 27, 33, 0.7)',
    highPointLabel: 'rgba(26, 27, 33, 0.7)',
    endpointName: 'rgba(26, 27, 33, 0.85)',
  },
};

export function elevationProfilePalette(theme: EmbedTheme | null | undefined): ElevationProfilePalette {
  return PALETTES[normalizeEmbedTheme(theme)];
}
